<script>
// Split button (Design System AAWZ): ação primária colada a um chevron que abre
// um menu de ações secundárias. Emite `click` na ação principal e `select` (com
// o item) ao escolher uma opção do menu. Itens com `danger: true` ganham um
// divisor acima e ficam em vermelho. Itens com `dividerTop: true` ganham apenas
// o divisor acima (sem cor), para separar grupos de ações (ex: exportar × importar).
export default {
  name: 'BaseSplitButton',
  props: {
    label: {
      type: String,
      required: true,
    },
    // ícone leading opcional na ação principal (ex: 'mdi-plus' / 'add-circle')
    leading: {
      type: String,
      default: '',
    },
    // [{ label, icon?, danger?, dividerTop?, loading?, tooltip? }]
    // `tooltip` explica o item no hover, mesmo contrato de `option.tooltip`
    // do BaseToggleButton. Item sem a chave nao ganha tooltip nenhum.
    items: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // AAWZ DS 2026 — uma tela só pode ter UMA ação primária verde. Sub-ações
    // de suporte (ex: "Novo patrimônio" dentro de uma seção quando a ação da
    // página já é outra, como "Nova Simulação") usam esta variante em vez do
    // preenchido verde padrão.
    outline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuOpen: false,
      // largura MÍNIMA do menu = largura do botão inteiro (main + toggle),
      // medida ao abrir. O menu cresce conforme o conteúdo (labels em uma linha)
      // e nunca fica mais estreito que o botão.
      menuWidth: 0,
    }
  },
  computed: {
    // true enquanto QUALQUER item estiver em loading (ex: Exportar em andamento).
    anyItemLoading() {
      return (this.items || []).some((it) => it && it.loading)
    },
    // Lista ja decorada com a classe e a cor de cada item. Existe pra o template
    // nao chamar funcao (CLAUDE.md §"Sem Chamada de Funcao no Template") e pra as
    // duas ramificacoes do item — com e sem tooltip — nao divergirem de estilo.
    // O id precisa ser unico no DOCUMENTO, nao dentro da instancia: a tela de
    // Financeiro monta DOIS splits e ambos tinham "Exportar historico" no indice
    // 1, entao os dois spans nasciam com o mesmo id e o aria-describedby resolvia
    // o primeiro em ordem de documento.
    tipIds() {
      return (this.items || []).map((_, i) => `az-split-tip-${this._uid}-${i}`)
    },
    itemsDecorados() {
      return (this.items || []).filter(Boolean).map((item) => ({
        item,
        classe: {
          'az-split__item--danger': item.danger,
          'az-split__item--divided': item.dividerTop && !item.danger,
        },
        // O hex e o valor que o CLAUDE.md do repo fixa para icone de acao
        // ("action icons must use color #8E8E8E only"); nao ha token CSS
        // equivalente exposto. Herdado do itemIconColor removido.
        cor: item.danger ? 'danger' : '#8E8E8E',
      }))
    },
  },
  watch: {
    menuOpen(open) {
      if (open && this.$refs.split) {
        this.menuWidth = this.$refs.split.offsetWidth
      }
    },
    // Fecha o menu quando a ação loadable TERMINA (loading true→false). Durante o
    // loading o menu fica aberto mostrando o spinner; ao concluir, fecha sozinho.
    anyItemLoading(now, was) {
      if (was && !now) this.menuOpen = false
    },
  },
  methods: {
    onItem(item) {
      // Evita duplo clique enquanto a ação do item está em andamento.
      if (item && item.loading) return
      this.$emit('select', item)
      // O menu usa close-on-content-click=false (senão fecharia antes do spinner
      // do item aparecer — bug: 1º clique fechava sem mostrar loading). Itens
      // "loadable" (declaram a chave `loading`, ex: Exportar) mantêm o menu aberto
      // p/ exibir o loading in-place; os demais (abrem modal/navegam) fecham.
      const isLoadable = item && Object.hasOwn(item, 'loading')
      if (!isLoadable) this.menuOpen = false
    },
  },
}
</script>

<template>
  <div ref="split" class="az-split" :class="{ 'az-split--outline': outline }">
    <button
      type="button"
      class="az-split__btn az-split__main"
      :disabled="disabled || loading"
      @click="$emit('click')"
    >
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="16"
        width="2"
        :color="outline ? 'primary' : 'white'"
      />
      <icon-svg
        v-else-if="leading"
        :name="leading"
        :size="15"
        :color="outline ? 'var(--primary)' : 'white'"
      />
      {{ label }}
    </button>
    <v-menu
      v-model="menuOpen"
      offset-y
      left
      :close-on-content-click="false"
      :nudge-bottom="6"
      :min-width="menuWidth"
      content-class="az-split-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="az-split__btn az-split__toggle"
          :disabled="disabled || loading"
          v-bind="attrs"
          v-on="on"
        >
          <icon-svg
            name="mdi-chevron-down"
            :size="16"
            :color="outline ? 'var(--primary)' : 'white'"
            class="az-split__chevron"
            :class="{ 'az-split__chevron--open': menuOpen }"
          />
        </button>
      </template>
      <div class="az-split__list">
        <template v-for="({ item, classe, cor }, i) in itemsDecorados">
          <!-- Item COM tooltip: o v-tooltip envolve o botao. Item SEM tooltip
               continua renderizando o botao direto, sem wrapper, pra nao mudar
               o DOM dos demais consumidores (mesma razao do v-if/v-else do
               BaseToggleButton). -->
          <!-- Abre pra ESQUERDA: o menu do split e alinhado a direita (`left` no
               v-menu), entao um tooltip pra direita esbarra na borda da janela e
               volta por cima do proprio menu, tapando os itens. -->
          <v-tooltip v-if="item.tooltip" :key="'tip-' + i" left max-width="320">
            <!-- O botao tem que ser o PRIMEIRO no do slot: o Vuetify posiciona o
                 overlay pelo activatorNode[0]. Inverter a ordem com o span quebra
                 o posicionamento em silencio. -->
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="az-split__item"
                :class="classe"
                :disabled="item.loading"
                v-bind="attrs"
                :aria-describedby="tipIds[i]"
                @click="onItem(item)"
                v-on="on"
              >
                <v-progress-circular
                  v-if="item.loading"
                  indeterminate
                  size="15"
                  width="2"
                  :color="cor"
                />
                <icon-svg
                  v-else-if="item.icon"
                  :name="item.icon"
                  :size="15"
                  :color="cor"
                />
                {{ item.label }}
              </button>
              <!-- O Vuetify 2.0.11 NAO emite aria-describedby (medido: a string
                   nao existe em node_modules/vuetify/lib), e o `attrs` do activator
                   so entrega role/aria-haspopup/aria-expanded. Sem este span o
                   leitor de tela ouve o label e perde a explicacao, que e o
                   proprio conteudo da entrega. -->
              <span :id="tipIds[i]" class="sr-only">
                {{ item.tooltip }}
              </span>
            </template>
            <span>{{ item.tooltip }}</span>
          </v-tooltip>

          <button
            v-else
            :key="i"
            type="button"
            class="az-split__item"
            :class="classe"
            :disabled="item.loading"
            @click="onItem(item)"
          >
            <v-progress-circular
              v-if="item.loading"
              indeterminate
              size="15"
              width="2"
              :color="cor"
            />
            <icon-svg
              v-else-if="item.icon"
              :name="item.icon"
              :size="15"
              :color="cor"
            />
            {{ item.label }}
          </button>
        </template>
      </div>
    </v-menu>
  </div>
</template>

<style scoped lang="sass">
.az-split
  display: inline-flex
  align-items: stretch

.az-split__btn
  display: inline-flex
  align-items: center
  justify-content: center
  gap: 8px
  height: var(--control-height)
  font-family: GeneralSans, sans-serif
  font-size: 14px
  font-weight: 600
  color: var(--text-on-primary)
  cursor: pointer
  background: var(--primary)
  border: 0
  transition: opacity 0.15s ease

  &:hover
    opacity: 0.88

  &:disabled
    cursor: not-allowed
    opacity: 0.5

.az-split__main
  padding: 0 16px
  border-radius: var(--radius-md) 0 0 var(--radius-md)

.az-split__toggle
  padding: 0 8px
  border-left: 1px solid rgba(255, 255, 255, 0.25)
  border-radius: 0 var(--radius-md) var(--radius-md) 0

// Variante outline (DS 2026) — mesmo layout, sem o preenchido verde. Borda e
// texto na cor da marca, igual a .btn-outline (_btn.sass) — não uma borda
// cinza inventada. Hover fica num tint leve (não inverte pra preenchido):
// o ícone principal/chevron têm cor fixa via prop (não currentColor), então
// um hover que vira texto branco os deixaria invisíveis sobre o próprio fundo
// verde.
.az-split--outline
  .az-split__btn
    color: var(--primary)
    background: transparent
    border-top: 1px solid var(--primary)
    border-bottom: 1px solid var(--primary)

    &:hover
      opacity: 1
      background: var(--success-bg-soft)

  .az-split__main
    border-left: 1px solid var(--primary)

  .az-split__toggle
    border-left: 1px solid var(--primary)
    border-right: 1px solid var(--primary)

.az-split__chevron
  transition: transform 0.2s ease

  &--open
    transform: rotate(180deg)

.az-split__list
  width: 100%
  padding: 4px

.az-split__item
  position: relative
  display: flex
  align-items: center
  gap: 8px
  width: 100%
  padding: 8px 10px
  font-family: Geist, sans-serif
  font-size: 13px
  color: var(--neutral-950)
  text-align: left
  white-space: nowrap
  cursor: pointer
  background: none
  border: 0
  border-radius: var(--radius-sm)

  &:hover
    background: var(--neutral-100)

  &:disabled
    cursor: not-allowed
    opacity: 0.6

  // Divisor acima do item (linha cheia). Usado para separar grupos de ações
  // (ex: exportações × importações) e também pela variante destrutiva.
  &--divided,
  &--danger
    margin-top: 5px

  &--divided::before,
  &--danger::before
    position: absolute
    top: -3px
    right: 0
    left: 0
    height: 1px
    content: ''
    background: var(--border-hairline)

  // item destrutivo: além do divisor acima, texto vermelho
  &--danger
    color: var(--danger-text)

// Texto só para leitor de tela. O Vuetify 2.0.11 não tem utility de sr-only
// (`grep -rl "sr-only" node_modules/vuetify/` = 0), então a classe é local.
// Fica no bloco SCOPED — ao contrário do `.v-menu__content`, este span é
// renderizado por este componente e carrega o `data-v-*`. Mesmo padrão de
// type-bars-horizontal.vue, que também declara a classe scoped.
.sr-only
  position: absolute
  width: 1px
  height: 1px
  padding: 0
  margin: -1px
  overflow: hidden
  clip: rect(0, 0, 0, 0)
  white-space: nowrap
  border: 0
</style>

<style lang="sass">
// O conteúdo do v-menu renderiza no body (fora do escopo), então o
// .v-menu__content não recebe o data-scope. Acrescenta a borda hairline; raio,
// fundo e sombra (Flutuante) já vêm do estilo global de .v-menu__content.
.v-menu__content.az-split-menu
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-md) !important
</style>
